import { PATH_LITERAL } from '@bartizo/shared';

/**
 * Turning a page from a top list into a block rule (P11.14).
 *
 * The lists hold *normalised* paths — `/orders/1234` is `/orders/:id` — so some
 * cannot be a rule: `:id` stands for many different pages, and a rule that matched
 * the text `:id` would match none of them. Those say why instead of offering a
 * button that would write a rule which does nothing, or (as a prefix) one that
 * blocks every order.
 */

export type Blockable = { ok: true } | { ok: false; reason: string };

export function blockablePath(path: string): Blockable {
  if (/(^|\/):(id|more)(\/|$)/.test(path)) {
    return {
      ok: false,
      reason:
        'This page has a changing part (shown as :id), so one rule cannot name it. Make a rule for a start or an ending of the address instead.',
    };
  }
  if (!path.startsWith('/')) return { ok: false, reason: 'Not a page address.' };
  if (!PATH_LITERAL.test(path)) {
    return {
      ok: false,
      reason:
        'This address has characters a rule cannot hold (spaces, quotes, braces). Name a shorter part of it in a rule.',
    };
  }
  if (path.length > 200) return { ok: false, reason: 'This address is longer than a rule holds.' };
  return { ok: true };
}

/**
 * The Rules page with the rule's path filled in, for the layer the Traffic page is
 * looking at (a server, a group, or every server), starting in watch mode: a rule
 * that counts what it would refuse for a day before it refuses anything.
 */
export function blockRuleLink(
  path: string,
  scope: { serverId?: string | undefined; groupId?: string | undefined },
): string {
  const params = new URLSearchParams({ path, mode: 'watch' });
  if (scope.serverId !== undefined) params.set('server', scope.serverId);
  else if (scope.groupId !== undefined) params.set('group', scope.groupId);
  return `/rules?${params.toString()}`;
}
